import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, BarChart3, CheckCircle2, ClipboardList, Flag, Settings, Target, type LucideIcon } from "lucide-react";
import { getFacilities, type FacilityOption } from "../../lib/services/facilities";
import { listQapiFindings, type QapiFinding, type QapiFindingStatus } from "../../lib/services/chartAudit";
import { summarizeQapiFindings } from "../../lib/services/qapiLeadership";

const STATUS_OPTIONS: Array<{ value: QapiFindingStatus | "all"; label: string }> = [
  { value: "all", label: "All statuses" },
  { value: "open", label: "Open" },
  { value: "in_review", label: "In review" },
  { value: "pip_created", label: "PIP created" },
  { value: "resolved", label: "Resolved" },
];

const SOURCE_OPTIONS = [
  { label: "Manual committee issue", detail: "Reference-only source. Backend intake remains future-scoped." },
  { label: "Daily assessment", detail: "Use daily assessment findings and corrective actions as source context." },
  { label: "Guided survey", detail: "Use visit findings, report gaps, and source checklist evidence." },
  { label: "Chart audit", detail: "Open the existing chart-audit QAPI workflow for status updates and PIP creation." },
  { label: "Training", detail: "Use training assignments, validation gaps, and reinforcement needs." },
  { label: "CAPA escalation", detail: "Use active corrective actions and overdue follow-up." },
];

const WORKSPACE_SECTIONS: Array<{ label: string; href: string; icon: LucideIcon }> = [
  { label: "Overview", href: "#overview", icon: Activity },
  { label: "Source Selection", href: "#source-selection", icon: Flag },
  { label: "Track Initiatives", href: "#track-initiatives", icon: ClipboardList },
  { label: "PIP / CAPA Links", href: "#pip-capa-links", icon: Target },
  { label: "Dashboard Report-Out", href: "#dashboard-report-out", icon: BarChart3 },
  { label: "QAPI Settings", href: "#qapi-settings", icon: Settings },
];

function formatStatus(status: QapiFindingStatus): string {
  return status.replace("_", " ");
}

function findFacilityName(facilities: FacilityOption[], facilityId: string): string {
  return facilities.find((facility) => facility.facility_id === facilityId)?.facility_name ?? "Selected facility";
}

export function QapiLeadershipWorkspacePage() {
  const [facilities, setFacilities] = useState<FacilityOption[]>([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState<QapiFindingStatus | "all">("all");
  const [selectedSource, setSelectedSource] = useState("Chart audit");
  const [findings, setFindings] = useState<QapiFinding[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [workspaceNotice, setWorkspaceNotice] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadFacilities() {
      try {
        const rows = await getFacilities();
        if (!cancelled) setFacilities(rows);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load facilities");
      }
    }

    loadFacilities();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadFindings() {
      try {
        setLoading(true);
        setError(null);
        const rows = await listQapiFindings({
          facilityId: selectedFacilityId === "all" ? undefined : selectedFacilityId,
          status: selectedStatus,
        });
        if (!cancelled) setFindings(rows);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load QAPI findings");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadFindings();
    return () => {
      cancelled = true;
    };
  }, [selectedFacilityId, selectedStatus]);

  const summary = useMemo(() => summarizeQapiFindings(findings), [findings]);
  const topIssues = useMemo(
    () => findings.filter((finding) => finding.status !== "resolved").slice(0, 3),
    [findings],
  );

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <section className="app-surface p-5 md:p-6">
        <p className="text-xs font-bold uppercase text-[var(--brand-secondary)]">QAPI Leadership</p>
        <div className="mt-2 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-brand text-3xl font-bold text-[var(--brand-deep)]">QAPI workspace</h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-600">
              Turn current operational signals into leadership review, PIP linkage, and report-out without changing the underlying source records.
            </p>
          </div>
          <Link
            to="/chart-audit/qapi"
            className="rounded-lg bg-[var(--brand-primary)] px-4 py-2 text-sm font-bold text-white hover:opacity-90"
          >
            Open chart-audit QAPI
          </Link>
        </div>
      </section>

      <nav className="grid gap-2 sm:grid-cols-3 lg:grid-cols-6" aria-label="QAPI workspace sections">
        {WORKSPACE_SECTIONS.map(({ label, href, icon: Icon }) => (
          <a
            key={label}
            href={href}
            onClick={() => {
              if (label === "Track Initiatives") {
                setWorkspaceNotice("Track Initiatives is showing the existing QAPI findings queue below.");
              }
            }}
            className="flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 hover:border-[var(--brand-primary)]"
          >
            <Icon size={16} />
            {label}
          </a>
        ))}
      </nav>

      <section id="overview" className="grid gap-4 md:grid-cols-4">
        {[
          ["Active initiatives", summary.active],
          ["Open issues", summary.open],
          ["PIP links", summary.pipCreated],
          ["High severity", summary.highSeverity],
        ].map(([label, value]) => (
          <div key={String(label)} className="app-surface p-4">
            <p className="text-xs font-black uppercase text-slate-500">{label}</p>
            <p className="mt-2 text-3xl font-bold text-[var(--brand-deep)]">{value}</p>
          </div>
        ))}
      </section>

      <section className="app-surface grid gap-4 p-5 md:grid-cols-3">
        <label className="text-xs font-black uppercase text-slate-500">
          Facility
          <select
            value={selectedFacilityId}
            onChange={(event) => setSelectedFacilityId(event.target.value)}
            className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700"
          >
            <option value="all">All facilities</option>
            {facilities.map((facility) => (
              <option key={facility.facility_id} value={facility.facility_id}>{facility.facility_name}</option>
            ))}
          </select>
        </label>

        <label className="text-xs font-black uppercase text-slate-500">
          Status
          <select
            value={selectedStatus}
            onChange={(event) => setSelectedStatus(event.target.value as QapiFindingStatus | "all")}
            className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>

        <label className="text-xs font-black uppercase text-slate-500">
          Source
          <select
            value={selectedSource}
            onChange={(event) => setSelectedSource(event.target.value)}
            className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700"
          >
            {SOURCE_OPTIONS.map((option) => (
              <option key={option.label} value={option.label}>{option.label}</option>
            ))}
          </select>
        </label>
      </section>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">{error}</div>
      ) : null}

      {workspaceNotice ? (
        <div role="status" className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm font-semibold text-blue-800">
          {workspaceNotice}
        </div>
      ) : null}

      <section id="source-selection" className="app-surface p-5">
        <div className="flex items-center gap-2">
          <Flag size={18} className="text-[var(--brand-primary)]" />
          <h2 className="text-lg font-bold text-[var(--brand-deep)]">Source Selection</h2>
        </div>
        <p className="mt-2 text-sm text-slate-600">
          Choose the leadership signal to review. This does not create a new QAPI record from this page.
        </p>
        <div className="mt-4 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {SOURCE_OPTIONS.map((source) => (
            <div
              key={source.label}
              className={`rounded-lg border p-4 text-left transition-colors ${
                selectedSource === source.label
                  ? "border-[var(--brand-primary)] bg-blue-50"
                  : "border-slate-200 bg-white hover:border-slate-300"
              }`}
            >
              <button
                type="button"
                onClick={() => {
                  setSelectedSource(source.label);
                  setWorkspaceNotice(
                    source.label === "Chart audit"
                      ? "Chart audit uses the existing Chart Audit QAPI workflow for changes."
                      : `${source.label} selected as leadership source context.`,
                  );
                }}
                className="block w-full text-left"
              >
                <p className="font-bold text-slate-900">{source.label}</p>
              </button>
              <p className="mt-1 text-sm text-slate-600">{source.detail}</p>
              {source.label === "Chart audit" ? (
                <Link
                  to="/chart-audit/qapi"
                  className="mt-3 inline-flex rounded-lg bg-[var(--brand-primary)] px-3 py-2 text-xs font-bold text-white hover:opacity-90"
                >
                  Open Chart Audit QAPI workflow
                </Link>
              ) : null}
            </div>
          ))}
        </div>
      </section>

      <section id="track-initiatives" className="app-surface p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <ClipboardList size={18} className="text-[var(--brand-primary)]" />
            <h2 className="text-lg font-bold text-[var(--brand-deep)]">Track Initiatives</h2>
          </div>
          <p className="text-sm font-semibold text-slate-500">{summary.resolved} resolved</p>
        </div>

        {loading ? (
          <p className="mt-4 text-sm text-slate-500">Loading QAPI initiatives...</p>
        ) : findings.length === 0 ? (
          <p className="mt-4 rounded-lg border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500">
            No QAPI initiatives match the current filters.
          </p>
        ) : (
          <div className="mt-4 overflow-hidden rounded-lg border border-slate-200">
            <table className="w-full divide-y divide-slate-200 text-left text-sm">
              <thead className="bg-slate-50 text-xs font-black uppercase text-slate-500">
                <tr>
                  <th className="px-4 py-3">Issue</th>
                  <th className="px-4 py-3">Owner</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Cadence</th>
                  <th className="px-4 py-3">Metric</th>
                  <th className="px-4 py-3">Evidence</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {findings.map((finding) => (
                  <tr key={finding.id}>
                    <td className="px-4 py-3">
                      <p className="font-bold text-slate-900">{finding.standard}</p>
                      <p className="mt-1 text-xs text-slate-500">{finding.domain}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {finding.status === "pip_created" ? "CAPA owner" : "QAPI lead"}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{formatStatus(finding.status)}</td>
                    <td className="px-4 py-3 text-slate-600">Monthly review</td>
                    <td className="px-4 py-3 text-slate-600">{finding.severity} severity</td>
                    <td className="px-4 py-3 text-slate-600">
                      {findFacilityName(facilities, finding.facility_id)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section id="pip-capa-links" className="grid gap-4 md:grid-cols-2">
        <div className="app-surface p-5">
          <div className="flex items-center gap-2">
            <Target size={18} className="text-[var(--brand-primary)]" />
            <h2 className="text-lg font-bold text-[var(--brand-deep)]">PIP / CAPA Links</h2>
          </div>
          <p className="mt-3 text-sm text-slate-600">
            {summary.pipCreated} initiative{summary.pipCreated === 1 ? "" : "s"} currently link to PIP corrective actions.
          </p>
          <Link to="/actions" className="mt-4 inline-flex rounded-lg border border-slate-200 px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50">
            Review CAPA queue
          </Link>
        </div>

        <div id="dashboard-report-out" className="app-surface p-5">
          <div className="flex items-center gap-2">
            <BarChart3 size={18} className="text-[var(--brand-primary)]" />
            <h2 className="text-lg font-bold text-[var(--brand-deep)]">Dashboard Report-Out</h2>
          </div>
          <div className="mt-4 space-y-3">
            {topIssues.length === 0 ? (
              <p className="text-sm text-slate-500">No active QAPI report-out items.</p>
            ) : topIssues.map((finding) => (
              <div key={finding.id} className="rounded-lg border border-slate-200 p-3">
                <p className="font-bold text-slate-900">{finding.standard}</p>
                <p className="mt-1 text-sm text-slate-600">
                  {finding.severity} severity, {formatStatus(finding.status)}. Next action: leadership review.
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="qapi-settings" className="app-surface p-5">
        <div className="flex items-center gap-2">
          <Settings size={18} className="text-[var(--brand-primary)]" />
          <h2 className="text-lg font-bold text-[var(--brand-deep)]">QAPI Settings</h2>
        </div>
        <div className="mt-3 flex items-start gap-2 rounded-lg bg-slate-50 p-4 text-sm text-slate-600">
          <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-emerald-600" />
          QAPI feature access remains controlled by facility feature flags and existing role/capability gates.
        </div>
      </section>
    </div>
  );
}
